/**
 * lessonStageProgress.js — Cycle 60 · Continue-card real-% support.
 *
 * Tracks per-lesson stage completion in localStorage so the "Continue
 * where you left off" card can render a real progress bar instead of
 * the illustrative 50 %.
 *
 * Stages (also mirror the Phase-C standard lesson flow):
 *   0 = not started
 *   1 = Learn         (content read)
 *   2 = Try           (worked example opened)
 *   3 = Practice      (2-3 low-stakes items done)
 *   4 = 7-Q Round     (round finished, any score)
 *   5 = Mastery Test  (mastery attempt logged)
 *   6 = Reward        (certificate / stamp claimed)
 *
 * Store shape:
 *   we_lesson_stage_progress_v1 = { [lessonCode]: { stage:int, updatedAt:iso } }
 */
const KEY = "we_lesson_stage_progress_v1";
const TOTAL_STAGES = 6;

function safeLoad() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch { return {}; }
}
function safeSave(map) {
  try { localStorage.setItem(KEY, JSON.stringify(map)); } catch { /* noop */ }
}

export function getLessonStage(code) {
  if (!code) return 0;
  const map = safeLoad();
  return Math.max(0, Math.min(TOTAL_STAGES, Number(map?.[code]?.stage) || 0));
}

/**
 * Set/advance the stage. Non-regressing by default — a caller can't
 * accidentally reset a child who has already reached stage 4 by
 * re-entering the Learn tab.
 */
export function setLessonStage(code, stage, { force = false } = {}) {
  if (!code) return;
  const map = safeLoad();
  const cur = Math.max(0, Math.min(TOTAL_STAGES, Number(map?.[code]?.stage) || 0));
  const nxt = Math.max(0, Math.min(TOTAL_STAGES, Number(stage) || 0));
  const next = force ? nxt : Math.max(cur, nxt);
  if (next === cur && !force) return;
  map[code] = { stage: next, updatedAt: new Date().toISOString() };
  safeSave(map);
}

export function markStage(code, stage) {
  // Convenience alias; identical to setLessonStage(code, stage).
  setLessonStage(code, stage);
}

/**
 * getLessonPercent — 0..100 based on current stage.
 * Returns 0 for a code we've never seen.
 */
export function getLessonPercent(code) {
  const s = getLessonStage(code);
  return Math.round((s / TOTAL_STAGES) * 100);
}

export const LESSON_STAGES = {
  LEARN: 1, TRY: 2, PRACTICE: 3, ROUND: 4, MASTERY: 5, REWARD: 6, TOTAL: TOTAL_STAGES,
};
